import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Dropdown.scss';

/**
 * A dropdown component that reveals a list of actions.
 * @param {object} props - The component props.
 * @param {Array<object>} props.actions - An array of action objects. Each object should have a 'label' and an 'onClick' handler.
 * @param {React.ReactNode} props.children - The trigger element for the dropdown.
 */
export default function Dropdown({ actions, children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Close dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTriggerClick = (e) => {
    e.stopPropagation();

    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }

    setIsOpen(!isOpen);
  };

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  // Clone the trigger element to attach the onClick handler and ref
  const trigger = children
    ? React.cloneElement(children, {
        ref: triggerRef,
        onClick: handleTriggerClick,
        'aria-haspopup': true,
        'aria-expanded': isOpen,
      })
    : null;

  const renderDropdownList = () => (
    <ul
      className="cds-dropdown-list"
      style={{
        position: 'fixed',
        zIndex: 99999,
        top: position.top,
        left: position.left,
      }}
    >
      {actions.map((action, index) => (
        <li
          key={index}
          className={`cds-dropdown-item ${action.disabled ? 'disabled' : ''}`}
          onMouseDown={(e) => handleActionClick(e, action)}
          role="menuitem"
          tabIndex={action.disabled ? -1 : 0}
        >
          {action.label}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="cds-dropdown" ref={dropdownRef}>
      {trigger}
      {isOpen && ReactDOM.createPortal(renderDropdownList(), document.body)}
    </div>
  );
}
